import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ChevronDown, ChevronUp, X, Plus, Trash2, Edit2, Lock, Calendar, CheckCircle, AlertTriangle, Filter } from 'lucide-react';
import {
  selectTasks,
  selectActiveTaskId,
  selectProjectStatus,
  selectIsLoading,
  selectTasksError
} from '../features/projectTasksSlice';
import { useProjectTasks } from '../hooks/useProjectTasks';
import { useSingleProject } from '../hooks/useSingleProject';
import './SalesTaskList.css';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../config/config.js';
import ProgressBar from 'react-bootstrap/ProgressBar';
import './custom.css';
import { getAuth, signInWithEmailAndPassword, signInWithPhoneNumber, RecaptchaVerifier } from "firebase/auth";
import {
  getBusinessDaysBetween,
  addBusinessDays,
  getNextWorkingDay,
  isWorkingDay,
  formatBusinessDays
} from '../utils/businessDayUtils';

// import DatePicker from 'react-datepicker'; // You'll need to install this package
const userRole = window.localStorage.getItem('projectUserRole');
const userName = window.localStorage.getItem('projectUserDisplayName');
const userUID = window.localStorage.getItem('projectUserUID');
const auth = getAuth();


// Task Filter Component
const TaskFilter = ({ onFilterChange, currentFilter }) => {
  const filterOptions = [
    { value: 'all', label: 'All Tasks' },
    { value: 'my-tasks', label: 'My Tasks Only' },
    { value: 'assigned-to-me', label: 'Assigned to Me' },
    { value: 'active-only', label: 'Active Tasks Only' }
  ];

  return (
    <div className="task-filter-container" style={{ minWidth: '200px' }}>
      <div className="d-flex align-items-center">
        <Filter size={16} className="me-2 text-muted" />
        <select
          value={currentFilter}
          onChange={(e) => onFilterChange(e.target.value)}
          className="form-select form-select-sm"
          style={{
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            fontSize: '14px'
          }}
        >
          {filterOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};



const TaskCompletionConfirmationPopup = ({ isOpen, onClose, onConfirm, taskName, isLoading = false, taskType = "task" }) => {
  if (!isOpen) return null;

  return (
    <div className="modal show d-block" tabIndex="-1" role="dialog">
      <div className="modal-dialog modal-md" role="document">
        <div className="modal-content">
          <div className="modal-header bg-warning text-white">
            <h5 className="modal-title d-flex align-items-center">
              <AlertTriangle size={20} className="me-2" />
              Confirm {taskType === 'task' ? 'Task' : 'Subtask'} Completion
            </h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={onClose}
              aria-label="Close"
              disabled={isLoading}
            >
              <X size={20} />
            </button>
          </div>

          <div className="modal-body text-center">
            <div className="mb-3">
              <CheckCircle size={48} className="text-success mb-2" />
            </div>
            <p className="mb-2">
              Are you sure you want to mark this {taskType} as complete?
            </p>
            <p className="text-muted small mb-3">
              <strong>"{taskName}"</strong>
            </p>
            <div className="alert alert-warning small mb-0">
              <strong>Note:</strong> Make sure you have added your latest update and attached all relevant documents.
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-success"
              onClick={onConfirm}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Completing...
                </>
              ) : (
                <>
                  <CheckCircle size={16} className="me-1" />
                  Yes, Complete {taskType === 'task' ? 'Task' : 'Subtask'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const TaskUpdatePopup = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  projectId,
  projectName, // ADD THIS PROP
  taskName, // ADD THIS PROP
  title = "Update Task",
  availableFolders = [
    { id: "mom", displayName: "MOM" },
    { id: "site-images", displayName: "Image Assets" },
    { id: "finance-docs", displayName: "Transactions / Reciepts" },
    { id: "mom", displayName: "Discussion Doc" }
  ]
}) => {
  const [taskData, setTaskData] = useState({
    ...initialData,
    description: initialData?.description || ''
  });

  const initializeEmptyFolderState = () => {
    const emptyState = {};
    availableFolders.forEach((folder, index) => {
      emptyState[index] = [];
    });
    return emptyState;
  };

  const [filesByFolder, setFilesByFolder] = useState(initializeEmptyFolderState);
  const [uploadProgress, setUploadProgress] = useState({});
  const [fileUrls, setFileUrls] = useState([]);
  const [previewUrls, setPreviewUrls] = useState(initializeEmptyFolderState);
  const [isUploading, setIsUploading] = useState(false);
  const [isAttachmentAccordionOpen, setIsAttachmentAccordionOpen] = useState(false);
  const [selectedQualityCheck, setSelectedQualityCheck] = useState('');
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  const options = [
    { id: "PocepGixlLZ9vJCxOcMEzKpxyLF2", name: "Reshma", email: "fabdizreshma@gmail.com" },
    { id: "ivvt6DaIHmWtlvo0JCLufLDfOAv2", name: "Sanjay", email: "sanjay3556k@gmail.com" },
    // Add email field to your options
  ];

  // Function to send email with actual file attachments
  const sendQualityCheckEmail = async (qcPerson, uploadedFiles) => {
    try {
      setIsSendingEmail(true);

      // Get user's Firebase Auth token for authentication
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }
      const token = await user.getIdToken();

      // Download files as base64 for email attachments
      const fileAttachments = await Promise.all(
        uploadedFiles.map(async (file) => {
          try {
            // Fetch the file from Firebase Storage URL
            const response = await fetch(file.url);
            const blob = await response.blob();
            
            // Convert blob to base64
            return new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => {
                const base64Data = reader.result.split(',')[1]; // Remove data:image/png;base64, prefix
                resolve({
                  filename: file.name,
                  content: base64Data,
                  encoding: 'base64',
                  contentType: file.type,
                  size: file.size
                });
              };
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            });
          } catch (error) {
            console.error(`Error downloading file ${file.name}:`, error);
            return null;
          }
        })
      );

      // Filter out failed downloads
      const validAttachments = fileAttachments.filter(att => att !== null);

      // Prepare email data
      const emailData = {
        to: qcPerson.email,
        subject: `Quality Check Assignment - ${taskName || 'Task'}`,
        html: `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
            <h2 style="color: #2c3e50;">Quality Check Assignment</h2>
            <p>Hi ${qcPerson.name},</p>
            <p>You've been assigned for quality check on the following task:</p>
            
            <div style="background-color: #f8f9fa; padding: 15px; border-radius: 5px; margin: 20px 0;">
              <p><strong>Project:</strong> ${projectName || 'Project'}</p>
              <p><strong>Task:</strong> ${taskName || 'Task'}</p>
              <p><strong>Description:</strong> ${taskData.description || 'No description provided'}</p>
            </div>

            ${validAttachments.length > 0 ? `
              <h3 style="color: #2c3e50;">Attached Documents:</h3>
              <p>Please find ${validAttachments.length} document(s) attached to this email.</p>
              <ul style="list-style-type: none; padding: 0;">
                ${validAttachments.map(file => `
                  <li style="margin: 10px 0; padding: 10px; background-color: #e9ecef; border-radius: 5px;">
                    <strong>📎 ${file.filename}</strong><br/>
                    <small>Size: ${(file.size / 1024).toFixed(2)} KB</small>
                  </li>
                `).join('')}
              </ul>
            ` : '<p><em>No documents attached</em></p>'}

            <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #dee2e6;">
              <p>You can also view all documents online:</p>
              <a href="https://one.fabdiz.com/project/${projectId}" 
                 style="display: inline-block; padding: 12px 24px; background-color: #007bff; color: white; text-decoration: none; border-radius: 5px;">
                View Project
              </a>
            </div>

            <p style="margin-top: 30px; color: #6c757d; font-size: 12px;">
              This is an automated email from Fabdiz One. Please do not reply to this email.
            </p>
          </div>
        `,
        attachments: validAttachments
      };

      // Send email via your backend endpoint
      const response = await fetch('/api/send-qc-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(emailData)
      });

      if (!response.ok) {
        throw new Error('Failed to send email');
      }

      console.log('Quality check email sent successfully with attachments');
      return true;
    } catch (error) {
      console.error('Error sending quality check email:', error);
      // Don't throw - we don't want to fail the whole operation if email fails
      return false;
    } finally {
      setIsSendingEmail(false);
    }
  };

  const getTotalFileCount = () => {
    return Object.values(filesByFolder || {}).reduce((total, files) => total + (files?.length || 0), 0);
  };

  const handleFileChange = (folderIndex, e) => {
    const selectedFiles = Array.from(e.target.files);
    e.target.value = '';

    setFilesByFolder(prev => ({
      ...prev,
      [folderIndex]: [...selectedFiles]
    }));

    const newProgress = { ...uploadProgress };
    selectedFiles.forEach(file => {
      const fileKey = `${folderIndex}:${file.name}`;
      newProgress[fileKey] = 0;
    });
    setUploadProgress(newProgress);

    const newPreviews = [];
    selectedFiles.forEach(file => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          newPreviews.push({
            name: file.name,
            url: reader.result
          });
          setPreviewUrls(prev => ({
            ...prev,
            [folderIndex]: [...newPreviews]
          }));
        };
        reader.readAsDataURL(file);
      } else {
        newPreviews.push({
          name: file.name,
          url: null
        });
      }
    });

    if (newPreviews.length > 0 && !newPreviews.some(p => p.url !== null)) {
      setPreviewUrls(prev => ({
        ...prev,
        [folderIndex]: [...newPreviews]
      }));
    }
  };

  const removeFile = (folderIndex, indexToRemove) => {
    if (!filesByFolder[folderIndex] || !previewUrls[folderIndex]) return;

    setFilesByFolder(prev => ({
      ...prev,
      [folderIndex]: (prev[folderIndex] || []).filter((_, index) => index !== indexToRemove)
    }));

    setPreviewUrls(prev => ({
      ...prev,
      [folderIndex]: (prev[folderIndex] || []).filter((_, index) => index !== indexToRemove)
    }));

    if (previewUrls[folderIndex] && previewUrls[folderIndex][indexToRemove]) {
      const removedFileName = previewUrls[folderIndex][indexToRemove].name;
      if (removedFileName) {
        const fileKey = `${folderIndex}:${removedFileName}`;
        const updatedProgress = { ...uploadProgress };
        if (fileKey in updatedProgress) {
          delete updatedProgress[fileKey];
          setUploadProgress(updatedProgress);
        }
      }
    }
  };

  const uploadFiles = async () => {
    setIsUploading(true);
    const uploadedUrls = [];

    try {
      for (let folderIndex = 0; folderIndex < availableFolders.length; folderIndex++) {
        const folder = availableFolders[folderIndex];
        const folderFiles = filesByFolder[folderIndex] || [];

        for (const file of folderFiles) {
          const fileKey = `${folderIndex}:${file.name}`;
          const timestamp = new Date().getTime();
          const storageRef = ref(storage, `${projectId}/${folder.id}/${timestamp}_${file.name}`);

          const uploadTask = uploadBytesResumable(storageRef, file);

          const uploadPromise = new Promise((resolve, reject) => {
            uploadTask.on('state_changed',
              (snapshot) => {
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                setUploadProgress(prev => ({
                  ...prev,
                  [fileKey]: progress
                }));
              },
              (error) => {
                console.error('Upload error:', error);
                reject(error);
              },
              async () => {
                const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
                uploadedUrls.push({
                  name: file.name,
                  url: downloadUrl,
                  path: storageRef.fullPath,
                  folder: folder.id,
                  size: file.size,
                  type: file.type,
                  uploadedAt: new Date().toISOString()
                });
                resolve();
              }
            );
          });

          await uploadPromise;
        }
      }

      setFileUrls(uploadedUrls);
      return uploadedUrls;
    } catch (error) {
      console.error('File upload error:', error);
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const qualityCheck = options.find(opt => opt.id === selectedQualityCheck);
      
      // First upload files if any are selected
      let uploadedFiles = [];
      if (getTotalFileCount() > 0) {
        uploadedFiles = await uploadFiles();
      }

      // Prepare submit data
      const submitData = {
        ...taskData,
        description: taskData.description.trim(),
        files: uploadedFiles,
        qualityCheck: qualityCheck ? {
          uid: qualityCheck.id,
          displayName: qualityCheck.name
        } : null,
      };

      // Submit task update (this will handle backend notification)
      await onSubmit(submitData);

      // If QC person is selected, send email with attachments
      if (qualityCheck && uploadedFiles.length > 0) {
        await sendQualityCheckEmail(qualityCheck, uploadedFiles);
      }

      // Reset state
      resetFiles();
      setTaskData({ description: "" });
      setSelectedQualityCheck("");
      onClose();
    } catch (error) {
      console.error('Submit error:', error);
      alert('Error submitting task update. Please try again.');
    }
  };

  const resetFiles = () => {
    setFilesByFolder(initializeEmptyFolderState());
    setPreviewUrls(initializeEmptyFolderState());
    setUploadProgress({});
    setFileUrls([]);

    availableFolders.forEach((_, index) => {
      const fileInput = document.getElementById(`file-input-${index}`);
      if (fileInput) {
        fileInput.value = '';
      }
    });
  };

  const resetModal = () => {
    onClose();
    resetFiles();
    setIsAttachmentAccordionOpen(false);
    setSelectedQualityCheck("");
  };

  const renderFolderFiles = (folderIndex) => {
    const folderFiles = previewUrls[folderIndex] || [];

    if (folderFiles.length === 0) {
      return <p className="text-muted small">No files selected</p>;
    }

    return folderFiles.map((preview, index) => (
      <div key={index} className="card mb-2">
        <div className="card-body p-2">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <span className="text-truncate">{preview.name}</span>
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              onClick={() => removeFile(folderIndex, index)}
              disabled={isUploading}
            >
              <X size={14} />
            </button>
          </div>

          {preview.url && (
            <img
              src={preview.url}
              alt={`Preview ${index + 1}`}
              className="img-fluid rounded mb-1"
              style={{ maxHeight: "100px" }}
            />
          )}

          {uploadProgress[`${folderIndex}:${preview.name}`] !== undefined && (
            <div className="progress" style={{ height: '3px' }}>
              <div
                className="progress-bar"
                role="progressbar"
                style={{ width: `${uploadProgress[`${folderIndex}:${preview.name}`]}%` }}
              />
            </div>
          )}
        </div>
      </div>
    ));
  };

  const handleQCchange = (e) => {
    setSelectedQualityCheck(e.target.value);
  };

  if (!isOpen) return null;

  return (
    <div className="modal show d-block" tabIndex="-1" role="dialog">
      <div className="modal-dialog" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{title}</h5>
            <button
              type="button"
              className="btn-close"
              onClick={resetModal}
              aria-label="Close"
            />
          </div>

          <div className="modal-body">
            <div className="mb-3">
              <textarea
                className="form-control"
                rows="4"
                value={taskData.description}
                onChange={(e) => setTaskData(prev => ({
                  ...prev,
                  description: e.target.value
                }))}
                placeholder="Add update details"
              />
            </div>

            <div className="accordion mb-3">
              <div className="accordion-item">
                <h2 className="accordion-header">
                  <button
                    className={`accordion-button ${!isAttachmentAccordionOpen ? 'collapsed' : ''}`}
                    type="button"
                    onClick={() => setIsAttachmentAccordionOpen(!isAttachmentAccordionOpen)}
                  >
                    Attachments {getTotalFileCount() > 0 ? `(${getTotalFileCount()})` : ''}
                  </button>
                </h2>
                <div className={`accordion-collapse collapse ${isAttachmentAccordionOpen ? 'show' : ''}`}>
                  <div className="accordion-body p-2">
                    {availableFolders.map((folder, index) => (
                      <div key={`folder-${index}`} className="card mb-3">
                        <div className="card-header py-2">
                          <h6 className="mb-0">
                            {folder.displayName} {(filesByFolder[index] && filesByFolder[index].length > 0) ?
                              `(${filesByFolder[index].length})` : ''}
                          </h6>
                        </div>
                        <div className="card-body">
                          <div className="mb-3">
                            <input
                              type="file"
                              multiple
                              className="form-control"
                              onChange={(e) => handleFileChange(index, e)}
                              disabled={isUploading}
                              aria-label={`Upload files to ${folder.displayName}`}
                              key={`file-input-${index}`}
                              id={`file-input-${index}`}
                            />
                          </div>
                          {renderFolderFiles(index)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {title?.includes("QC files submission") && (
              <div className="mb-3">
                <label className="form-label">Select QC Person</label>
                <select
                  className="form-select"
                  value={selectedQualityCheck}
                  onChange={handleQCchange}
                >
                  <option value="">Select a QC Person...</option>
                  {options.map(designer => (
                    <option key={designer.id} value={designer.id}>
                      {designer.name}
                    </option>
                  ))}
                </select>
                {selectedQualityCheck && (
                  <small className="text-muted">
                    An email with attached documents will be sent to the selected QC person
                  </small>
                )}
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-primary w-100"
              onClick={handleSubmit}
              disabled={!taskData.description.trim() || isUploading || isSendingEmail}
            >
              {isUploading ? 'Uploading Files...' : 
               isSendingEmail ? 'Sending Email...' : 
               'Submit Update'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const DesignerAssignmentPopup = ({ isOpen, onClose, onAssign, isLoading }) => {
  const [selectedDesigner, setSelectedDesigner] = useState('');

  // Options array for designers
  const options = [
    { id: "lRuUNBhzb9TheRaVDiJwCItb1em2", name: "Vimla" },
    { id: "qvzMCFUePDbsVJm8udl7mzjlP7x1", name: "Sona Bakale" },
    { id: "Dexb50Y8CGeiZDvtor2mK7pT78U2", name: "Rishav" },
    { id: "RiMwhPsnAGewydLyD7ft2XtAhe13", name: "Nishita" },
    // { id: "heOAz54UbmUsaFJPYeryGRThZkA2", name: "Sanjay Kumar"}
  ];

  if (!isOpen) return null;

  const handleDesignerChange = (e) => {
    setSelectedDesigner(e.target.value);
  };

  const handleSubmit = () => {
    const designer = options.find(opt => opt.id === selectedDesigner);
    if (designer) {
      onAssign({
        designerId: designer.id,
        designerName: designer.name
      });
      setSelectedDesigner('');
    }
  };

  return (
    <div className="popup-overlay">
      <div className="popup-content">
        <div className="popup-header">
          <h3 className="popup-title">Assign Design Head</h3>
          <button className="close-btn" onClick={onClose}><X size={20} /></button>
        </div>

        <div>
          {/* <label className="input-label">Select Designer</label> */}
          <select
            className="popup-select"
            value={selectedDesigner}
            onChange={handleDesignerChange}
          >
            <option value="">Select a design Head...</option>
            {options.map(designer => (
              <option key={designer.id} value={designer.id}>
                {designer.name}
              </option>
            ))}
          </select>
        </div>

        <button
          className="popup-submit-btn"
          disabled={!selectedDesigner || isLoading}
          onClick={handleSubmit}
        >
          {isLoading ? 'Assigning...' : 'Assign Design Head'}
        </button>
      </div>
    </div>
  );
};

const ExecutionHeadAssignmentPopup = ({ isOpen, onClose, onAssign, isLoading }) => {
  const [selectedExecutionHead, setSelectedExecutionHead] = useState('');

  // Options array for Execution Head
  const options = [
    { id: "PV4tQohnD4g3kvaL4it8UFqQ7uW2", name: "Vijay Singh" }
  ];

  if (!isOpen) return null;

  const handleDesignerChange = (e) => {
    setSelectedExecutionHead(e.target.value);
  };

  const handleSubmit = () => {
    const designer = options.find(opt => opt.id === selectedExecutionHead);
    if (designer) {
      onAssign({
        executionHeadId: designer.id,
        executionHeadName: designer.name
      });
      setSelectedExecutionHead('');
    }
  };

  return (
    <div className="popup-overlay">
      <div className="popup-content">
        <div className="popup-header">
          <h3 className="popup-title">Assign Execution Head</h3>
          <button className="close-btn" onClick={onClose}><X size={20} /></button>
        </div>

        <div>
          {/* <label className="input-label">Select Designer</label> */}
          <select
            className="popup-select"
            value={selectedExecutionHead}
            onChange={handleDesignerChange}
          >
            <option value="">Select an Execution Head...</option>
            {options.map(designer => (
              <option key={designer.id} value={designer.id}>
                {designer.name}
              </option>
            ))}
          </select>
        </div>

        <button
          className="popup-submit-btn"
          disabled={!selectedExecutionHead || isLoading}
          onClick={handleSubmit}
        >
          {isLoading ? 'Assigning...' : 'Assign Execution Head'}
        </button>
      </div>
    </div>
  );
};

const TeamAssignmentPopup = ({ isOpen, onClose, onAssign, isLoading }) => {
  const [selectedTeam, setSelectedTeam] = useState({
    siteMeasurement: '',
     designerHead:'',
      designer:'',
      production:'',
      // qualityCheck:'',
  });

  // Options arrays for different roles
  const options = {
    siteMeasurement: [
      { id: "ULvyYnnIDeTajBAeg7PRe3ieE6x2", name: "Ashith N" }
    ],
    designers: [
      { id: "YUpQaAF77mO8hUmC5dgRK0wFCif2", name: "Akshata Patil" },
      { id: "FGbfZtqsj3OVq3yTsOwWml5NBRA3", name: "Akhil Josekutty" },
      { id: "DGZ6g1MDpUgo3tH7WK8vgw6PAwr1", name: "Chaitanya V" },
      // { id: "qFPBerhnQURjzs9MsH3KuJmiazp2", name: "Prithi Sridharan" },
      // { id: "lFUimxY44cXpJwimtFh1r4G6D3s2", name: "Preeti Niranjan" },
      { id: "iKp7E8gn4SQXXuF9j0Y8BK4TAff1", name: "Shalini" },
      { id: "7k9tFxIkAuhIGINJ4awNeMzm4h23", name: "Ibrahim" },
      { id: "SLRt0LwJqGVnYQ0ublia5NMWIQ12", name: "Afshan Hussain" },
      { id: "BgnvKVeuMugTr2Mz6auE5YihShM2", name: "Vinitha" },
      { id: "vzuhUIDpoNbFxt16ndZiY449dcv2", name: "Paramsheela" },
      { id: "mqufJ8dJA5dD83Txkq6hTL6LFi32", name: "Henthoi" },
      { id: "ncEeWK4izTPvaNE6LnF1Ayg06w13", name: "Rhea" },
      { id: "9UT82FQzPfUssDX4raJXGlk0AXm1", name: "Keerthi" },
      { id: "uTRE27Z1oGV4bPAQLzgBKUYqfkm1", name: "Jaydev" },
      { id: "jBwne0dzxOP5c578ziPK3O8uw4w1", name: "Aayushi" }
    ],
    juniorDesigners: [
      { id: null, name: null }
      // { id: "pp9L3HzpiRN2OCBGqFXpBN19aQA3", name: "Dhruthi" }
    ],
    productionDrawing: [
      { id: "evbQlJ9q2fT8T66jFpOLqXapLp53", name: "Rahul.P" },
      // { id: "pyup9vBVaUVWiV4rmsR55yjow4m1", name: "Rekha" }
    ]
    // ,
    // qualityCheck: [
    //   { id: "PocepGixlLZ9vJCxOcMEzKpxyLF2", name: "Reshma R" }
    // ]
  };




  if (!isOpen) return null;

  const handleTeamChange = (role, value) => {
    setSelectedTeam(prev => ({
      ...prev,
      [role]: value
    }));
  };

  const handleSubmit = () => {
    const teamData = {
      siteMeasurement: options.siteMeasurement.find(opt => opt.id === selectedTeam.siteMeasurement),
      designer: options.designers.find(opt => opt.id === selectedTeam.designer),
      juniorDesigner: options.juniorDesigners.find(opt => opt.id === selectedTeam.juniorDesigner),
      productionDrawing: options.productionDrawing.find(opt => opt.id === selectedTeam.productionDrawing),
      // qualityCheck: options.qualityCheck.find(opt => opt.id === selectedTeam.qualityCheck)
    };

    onAssign({
      siteMeasurement: {
        uid: teamData.siteMeasurement?.id,
        displayName: teamData.siteMeasurement?.name
      },
      designerHead: {
        uid: window.localStorage.getItem('projectUserUID'),
        displayName: window.localStorage.getItem('projectUserDisplayName')
      },
       designer: {
        uid: teamData.designer?.id,
        displayName: teamData.designer?.name
      },
      production: {
        uid: teamData.productionDrawing?.id,
        displayName: teamData.productionDrawing?.name
      }
      // ,
      // qualityCheck: {
      //   uid: teamData.qualityCheck?.id,
      //   displayName: teamData.qualityCheck?.name
      // }
    });

    setSelectedTeam({
      siteMeasurement: '',
      designerHead:'',
      designer:'',
      production:'',
      // qualityCheck:'',
    });
  };


    // Updated validation - junior designer is now optional
  const isTeamValid =
    selectedTeam.siteMeasurement &&
    selectedTeam.designer &&
    selectedTeam.productionDrawing 
    // &&
    // selectedTeam.qualityCheck;
  // Note: juniorDesigner removed from validation


  const isFormValid = isTeamValid;



  return (
    <div className="popup-overlay">
      <div className="popup-content max-w-lg w-full">
        <div className="popup-header">
          <h3 className="popup-title">Assign Team Members</h3>
          <button className="close-btn" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="space-y-4 mb-2">
          {/* Site Measurement Select */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Site Measurement
            </label>
            <select
              className="w-100 rounded-md border border-gray-300 p-2"
              value={selectedTeam.siteMeasurement}
              onChange={(e) => handleTeamChange('siteMeasurement', e.target.value)}
            >
              <option value="">Select site measurement staff...</option>
              {options.siteMeasurement.map(person => (
                <option key={person.id} value={person.id}>
                  {person.name}
                </option>
              ))}
            </select>
            
          </div>

            <div className="form-group mb-3">
                      <label className="form-label">Senior Designer <span className="text-danger">*</span></label>
                      <select
                        className="form-control form-control-sm"
                        value={selectedTeam.designer}
                        onChange={(e) => handleTeamChange('designer', e.target.value)}
                      >
                        <option value="">Select designer...</option>
                        {options.designers.map(person => (
                          <option key={person.id} value={person.id}>
                            {person.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group mb-3">
                      <label className="form-label">Junior Designer <span className="text-muted">(Optional)</span></label>
                      <select
                        className="form-control form-control-sm"
                        value={selectedTeam.juniorDesigner}
                        onChange={(e) => handleTeamChange('juniorDesigner', e.target.value)}
                      >
                        <option value="">Select junior designer...</option>
                        {options.juniorDesigners.map(person => (
                          <option key={person.id} value={person.id}>
                            {person.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group mb-3">
                      <label className="form-label">Production Drawing <span className="text-danger">*</span></label>
                      <select
                        className="form-control form-control-sm"
                        value={selectedTeam.productionDrawing}
                        onChange={(e) => handleTeamChange('productionDrawing', e.target.value)}
                      >
                        <option value="">Select production staff...</option>
                        {options.productionDrawing.map(person => (
                          <option key={person.id} value={person.id}>
                            {person.name}
                          </option>
                        ))}
                      </select>
                    </div>

                   

        </div>

        <div className="mt-6">
          <button
            className="popup-submit-btn"
            disabled={!isFormValid || isLoading}
            onClick={handleSubmit}
          >
            {isLoading ? 'Assigning Team...' : 'Assign Team'}
          </button>
        </div>
      </div>
    </div>
  );
};


const TeamTaskAssignmentPopup = ({ isOpen, onClose, onAssignTeamTask, isLoading }) => {
  const [selectedTeam, setSelectedTeam] = useState({
    designer: '',
    juniorDesigner: '',
    productionDrawing: ''
  });

  const [tasks, setTasks] = useState([
    // {
    //   name: 'Design Meeting', role: 'designer', order: 100, phase: 'design', plannedStartDate: null, plannedFinishDate: null, actualStartDate: null, actualFinishDate: null, stageName: '3D Modelling', stageNo: 1, stagePercentage: 20, folders: [{ id: "mom", displayName: 'MOM' }, { id: "presentation-renders", displayName: '3D Presentation (PDF)' }], subtasks: [
    //     { id: '1stcutmeetingsubtask', name: 'MOM', descriptions: [], files: [], folders: [{ id: "mom", displayName: 'MOM' }, { id: "presentation-renders", displayName: '3D Presentation (PDF)' }], completed: false },
    //   ]
    // },
    // {
    //   name: '3D Model Finalization', role: 'designer', order: 110, phase: 'design', plannedStartDate: null, plannedFinishDate: null, stageName: '3D Modelling', stageNo: 1, stagePercentage: 40, folders: [{ id: 'mom', displayName: 'MOM' }, { id: 'presentation-renders', displayName: '3D Presentation (PDF)' }], subtasks: [
    //     { id: '3diteration', name: '3D Iteration', descriptions: [], files: [], folders: [{ id: 'mom', displayName: 'MOM' }, { id: 'presentation-renders', displayName: '3D Presentation (PDF)' }], completed: false },
    //     { id: '3dmeeting', name: '3D Meeting', descriptions: [], files: [], folders: [{ id: 'mom', displayName: 'MOM' }, { id: 'presentation-renders', displayName: '3D Presentation (PDF)' }], completed: false },
    //   ]
    // },
    // {
    //   name: 'Material Selection', role: 'designer', order: 120, phase: 'design', plannedStartDate: null, plannedFinishDate: null, stageName: 'Material Selection', stageNo: 2, stagePercentage: 44, folders: [{ id: 'mom', displayName: 'MOM' }, { id: 'presentation-renders', displayName: 'Material Images (PDF)' }], subtasks: [
    //     { id: 'msmom', name: 'MOM', descriptions: [], files: [], folders: [{ id: 'mom', displayName: 'MOM' }, { id: 'presentation-renders', displayName: 'Material Images (PDF)' }], completed: false },
    //   ]
    // },
    // { name: 'Quotation Discussion', role: 'designer', order: 130, phase: 'design', plannedStartDate: null, plannedFinishDate: null, stageName: 'Costing', stageNo: 2, stagePercentage: 46, folders: [{ id: 'quotes', displayName: 'Quote' }, { id: 'mom', displayName: 'MOM' }] },
    // { name: 'Quote and Design Revision', role: 'designer', order: 140, phase: 'design', plannedStartDate: null, plannedFinishDate: null, stageName: 'Costing', stageNo: 2, stagePercentage: 48, folders: [{ id: 'quotes', displayName: 'Quote' }] },
    // { name: '40% Payment Collection', role: 'designer', order: 150, phase: 'design', plannedStartDate: null, plannedFinishDate: null, stageName: 'Payment Collection', stageNo: 2, stagePercentage: 50, folders: [{ id: 'finance-docs', displayName: 'Invoice' }, { id: 'finance-docs', displayName: 'Reciept' }] },
    // { name: '2D Validation', role: 'production', order: 170, phase: 'design', plannedStartDate: null, plannedFinishDate: null, stageName: 'Validation', stageNo: 3, stagePercentage: 66, folders: [{ id: 'presentation-renders', displayName: '2D Layouts (PDF)' }, { id: 'mom', displayName: 'MOM' }] },
    // {
    //   name: 'Site Validation', role: 'designer', order: 180, phase: 'design', plannedStartDate: null, plannedFinishDate: null, stageName: 'Prod Drawing, QC and sign off', stageNo: 3, stagePercentage: 71, folders: [{ id: 'site-images', displayName: 'Site Images' }, { id: 'presentation-renders', displayName: '2D Layouts (PDF)' }, { id: 'mom', displayName: 'MOM' }], subtasks: [
    //     { id: '1stcutmeetingsubtask', name: 'MOM', descriptions: [], files: [], folders: [{ id: 'site-images', displayName: 'Site Images' }, { id: 'presentation-renders', displayName: '2D Layouts (PDF)' }, { id: 'mom', displayName: 'MOM' }], completed: false },
    //   ]
    // },
    { name: 'Design Meeting', role: 'designer', order: 100, phase: 'design', plannedStartDate: null, plannedFinishDate: null, stageName: 'Render', stageNo: 3, stagePercentage: 59, folders: [{ id: 'presentation-renders', displayName: '3D Presentation (PDF)' }] },
    { name: 'Material Selection', role: 'designer', order: 110, phase: 'design', plannedStartDate: null, plannedFinishDate: null, stageName: 'Render', stageNo: 3, stagePercentage: 59, folders: [{ id: 'presentation-renders', displayName: '3D Presentation (PDF)' }] },
    { name: 'Quotation Revise', role: 'designer', order: 120, phase: 'design', plannedStartDate: null, plannedFinishDate: null, stageName: 'Render', stageNo: 3, stagePercentage: 59, folders: [{ id: 'presentation-renders', displayName: '3D Presentation (PDF)' }] },
    { name: 'Quote discussion and changes', role: 'designer', order: 130, phase: 'design', plannedStartDate: null, plannedFinishDate: null, stageName: 'Render', stageNo: 3, stagePercentage: 59, folders: [{ id: 'presentation-renders', displayName: '3D Presentation (PDF)' }] },
    { name: '40% Payment Collection', role: 'designer', order: 140, phase: 'design', plannedStartDate: null, plannedFinishDate: null, stageName: 'Render', stageNo: 3, stagePercentage: 59, folders: [{ id: 'presentation-renders', displayName: '3D Presentation (PDF)' }] },
    { name: 'Validation 2D drawings', role: 'designer', order: 150, phase: 'design', plannedStartDate: null, plannedFinishDate: null, stageName: 'Render', stageNo: 3, stagePercentage: 59, folders: [{ id: 'presentation-renders', displayName: '3D Presentation (PDF)' }] },
    { name: 'Validation on site', role: 'designer', order: 160, phase: 'design', plannedStartDate: null, plannedFinishDate: null, stageName: 'Render', stageNo: 3, stagePercentage: 59, folders: [{ id: 'presentation-renders', displayName: '3D Presentation (PDF)' }] },
    { name: 'Validation points update in 2D and 3D', role: 'designer', order: 170, phase: 'design', plannedStartDate: null, plannedFinishDate: null, stageName: 'Render', stageNo: 3, stagePercentage: 59, folders: [{ id: 'presentation-renders', displayName: '3D Presentation (PDF)' }] },
    { name: '3D Renders', role: 'production', order: 180, phase: 'design', plannedStartDate: null, plannedFinishDate: null, stageName: 'Render', stageNo: 3, stagePercentage: 59, folders: [{ id: 'presentation-renders', displayName: '3D Presentation (PDF)' }] },
    { name: '2D Production Drawing', role: 'production', order: 190, phase: 'design', plannedStartDate: null, plannedFinishDate: null, stageName: 'Render', stageNo: 3, stagePercentage: 59, folders: [{ id: 'presentation-renders', displayName: '3D Presentation (PDF)' }] },
    { name: 'QC files submission', role: 'designer', order: 200, phase: 'design', plannedStartDate: null, plannedFinishDate: null, stageName: 'Render', stageNo: 3, stagePercentage: 59, folders: [{ id: 'presentation-renders', displayName: '3D Presentation (PDF)' }] },
    { name: 'QC Check', role: 'qualityCheck', order: 210, phase: 'design', plannedStartDate: null, plannedFinishDate: null, stageName: 'Render', stageNo: 3, stagePercentage: 59, folders: [{ id: 'presentation-renders', displayName: '3D Presentation (PDF)' }] },
    { name: 'QC corrections', role: 'qualityCheck', order: 220, phase: 'design', plannedStartDate: null, plannedFinishDate: null, stageName: 'Render', stageNo: 3, stagePercentage: 59, folders: [{ id: 'presentation-renders', displayName: '3D Presentation (PDF)' }] },
    { name: 'Project Head QC', role: 'designer', order: 230, phase: 'design', plannedStartDate: null, plannedFinishDate: null, stageName: 'Render', stageNo: 3, stagePercentage: 59, folders: [{ id: 'presentation-renders', displayName: '3D Presentation (PDF)' }] },
    { name: 'Project Head QC Corrections', role: 'designer', order: 240, phase: 'design', plannedStartDate: null, plannedFinishDate: null, stageName: 'Render', stageNo: 3, stagePercentage: 59, folders: [{ id: 'presentation-renders', displayName: '3D Presentation (PDF)' }] },
    { name: 'Production documents preparations and approval from client', role: 'designer', order: 250, phase: 'design', plannedStartDate: null, plannedFinishDate: null, stageName: 'Render', stageNo: 3, stagePercentage: 59, folders: [{ id: 'presentation-renders', displayName: '3D Presentation (PDF)' }] },
    { name: 'Production files submission with seal and sign', role: 'designer', order: 260, phase: 'design', plannedStartDate: null, plannedFinishDate: null, stageName: 'Render', stageNo: 3, stagePercentage: 59, folders: [{ id: 'presentation-renders', displayName: '3D Presentation (PDF)' }] },
    // { name: 'Quote and Design Revision', role: 'designer', order: 190, phase: 'design', plannedStartDate: null, plannedFinishDate: null, stageName: 'Costing', stageNo: 2, stagePercentage: 48, folders: [{ id: 'quotes', displayName: 'Quote' }] },
    // { name: 'Production Drawing', role: 'production', order: 200, phase: 'design', plannedStartDate: null, plannedFinishDate: null, stageName: 'Prod Drawing, QC and sign off', stageNo: 3, stagePercentage: 85, folders: [{ id: 'presentation-renders', displayName: '2D Layouts (PDF)' }, { id: 'quotes', displayName: 'Quote' }] },
    // { name: 'Quality Check', role: 'qualityCheck', order: 210, phase: 'design', plannedStartDate: null, plannedFinishDate: null, stageName: 'Prod Drawing, QC and sign off', stageNo: 4, stagePercentage: 90, folders: [{ id: 'quotes', displayName: 'Quote' }, { id: 'presentation-renders', displayName: 'Drawings' }] },
    // { name: 'Quote and Design Revision', role: 'designer', order: 220, phase: 'design', plannedStartDate: null, plannedFinishDate: null, stageName: 'Costing', stageNo: 2, stagePercentage: 48, folders: [{ id: 'quotes', displayName: 'Quote' }] },
    // { name: 'Create Sign Off Document', role: 'designer', order: 230, phase: 'design', plannedStartDate: null, plannedFinishDate: null, stageName: 'Prod Drawing, QC and sign off', stageNo: 4, stagePercentage: 95, folders: [{ id: 'mom', displayName: 'MOM' }] },
    // { name: 'Sign Off - Client', role: 'designer', order: 240, phase: 'design', plannedStartDate: null, plannedFinishDate: null, stageName: 'Prod Drawing, QC and sign off', stageNo: 4, stagePercentage: 95, folders: [{ id: 'mom', displayName: 'MOM' }] },
    // { name: 'Send for Production', role: 'designer', order: 250, phase: 'design', plannedStartDate: null, plannedFinishDate: null, stageName: 'Prod Drawing, QC and sign off', stageNo: 4, stagePercentage: 100, folders: [{ id: 'mom', displayName: 'MOM' }] }
  
  ]);

  const options = {
    designers: [
      { id: "YUpQaAF77mO8hUmC5dgRK0wFCif2", name: "Akshata Patil" },
      { id: "FGbfZtqsj3OVq3yTsOwWml5NBRA3", name: "Akhil Josekutty" },
      { id: "DGZ6g1MDpUgo3tH7WK8vgw6PAwr1", name: "Chaitanya V" },
      { id: "5EE0Kz3QcQTuG4QYvvABuMmjEgz1", name: "Nanditha Reddy" },
      { id: "LDcFAckbGhPnnq8xnxBZ2Hl9fus1", name: "Robert K Mathew" },
      { id: "qFPBerhnQURjzs9MsH3KuJmiazp2", name: "Prithi Sridharan" },
      { id: "lFUimxY44cXpJwimtFh1r4G6D3s2", name: "Preeti Niranjan" },
      { id: "iKp7E8gn4SQXXuF9j0Y8BK4TAff1", name: "Shalini" },
      { id: "qLDBUFWFDVTuq9gYULCJ5cCfq973", name: "Shivangi Biju" },
      { id: "7k9tFxIkAuhIGINJ4awNeMzm4h23", name: "Ibrahim" },
      { id: "SLRt0LwJqGVnYQ0ublia5NMWIQ12", name: "Afshan Hussain" },
      { id: "BgnvKVeuMugTr2Mz6auE5YihShM2", name: "Vinitha" }
    ],
    juniorDesigners: [
      { id: null, name: null }
    ],
    productionDrawing: [
      { id: "evbQlJ9q2fT8T66jFpOLqXapLp53", name: "Rahul.P" },
    ],
    qualityCheck: [
      { id: "PocepGixlLZ9vJCxOcMEzKpxyLF2", name: "Reshma R" }
    ]
  };

  if (!isOpen) return null;

  const handleTeamChange = (role, value) => {
    setSelectedTeam(prev => ({
      ...prev,
      [role]: value
    }));
  };

  const handleTaskDateChange = (index, field, dateValue) => {
    setTasks(prevTasks => {
      const updatedTasks = [...prevTasks];
      const date = dateValue ? new Date(dateValue) : null;
      updatedTasks[index] = {
        ...updatedTasks[index],
        [field]: date
      };

      return updatedTasks;
    });
  };

  // Get minimum date for a task based on previous task's end date
  const getMinDate = (index, field) => {
    if (index === 0) return ''; // First task has no restrictions
    
    const prevTask = tasks[index - 1];
    if (!prevTask.plannedFinishDate) return '';
    
    // Both start and end dates of current task must be >= previous task's end date
    return prevTask.plannedFinishDate.toISOString().split('T')[0];
  };

  const handleSubmit = () => {
    const teamData = {
      designer: options.designers.find(opt => opt.id === selectedTeam.designer),
      juniorDesigner: options.juniorDesigners.find(opt => opt.id === selectedTeam.juniorDesigner),
      productionDrawing: options.productionDrawing.find(opt => opt.id === selectedTeam.productionDrawing),
      qualityCheck: options.qualityCheck.find(opt => opt.id === selectedTeam.qualityCheck)
    };

    const assignments = {
      designer: {
        uid: teamData.designer?.id,
        displayName: teamData.designer?.name
      },
      production: {
        uid: teamData.productionDrawing?.id,
        displayName: teamData.productionDrawing?.name
      },
      qualityCheck: {
        uid: teamData.qualityCheck?.id,
        displayName: teamData.qualityCheck?.name
      }
    };

    if (teamData.juniorDesigner) {
      assignments.juniorDesigner = {
        uid: teamData.juniorDesigner.id,
        displayName: teamData.juniorDesigner.name
      };
    }

    const firstTaskStartDate = tasks.length > 0 ? tasks[0].plannedStartDate : null;
    const lastTaskFinishDate = tasks.length > 0 ? tasks[tasks.length - 1].plannedFinishDate : null;
    const sixthTaskStartDate = tasks.length >= 6 ? tasks[5].plannedStartDate : null;

    onAssignTeamTask({
      tasks: tasks,
       username: userName || 'system',
      firstTaskStartDate,
      lastTaskFinishDate,
      sixthTaskStartDate
    });

    setSelectedTeam({
      designer: '',
      juniorDesigner: '',
      productionDrawing: '',
      qualityCheck: ''
    });
  };

  // const isTeamValid = selectedTeam.designer && selectedTeam.productionDrawing && selectedTeam.qualityCheck;
  const areTasksValid = tasks.every(task => task.plannedStartDate && task.plannedFinishDate);
  // const isFormValid = isTeamValid && areTasksValid;
  const isFormValid =  areTasksValid;

  return (
    <div className="modal" style={{ display: isOpen ? 'block' : 'none', backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1050 }}>
      <div className="modal-dialog modal-xl modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Project Assignment and Scheduling</h5>
            <button type="button" className="close" onClick={onClose}>
              <span>&times;</span>
            </button>
          </div>

          <div className="modal-body">
            <div className="container-fluid">
              <div className="row">
                <div className="col-12">
                  <h6 className="mb-3">Task Timeline</h6>
                  <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <table className="table table-sm table-striped">
                      <thead>
                        <tr>
                          <th>Task</th>
                          <th>Start Date</th>
                          <th>End Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tasks.map((task, index) => (
                          <tr key={index}>
                            <td>{task.name}</td>
                            <td>
                              <input
                                type="date"
                                className="form-control form-control-sm"
                                value={task.plannedStartDate ? task.plannedStartDate.toISOString().split('T')[0] : ''}
                                onChange={(e) => handleTaskDateChange(index, 'plannedStartDate', e.target.value)}
                                min={getMinDate(index, 'plannedStartDate')}
                              />
                            </td>
                            <td>
                              <input
                                type="date"
                                className="form-control form-control-sm"
                                value={task.plannedFinishDate ? task.plannedFinishDate.toISOString().split('T')[0] : ''}
                                onChange={(e) => handleTaskDateChange(index, 'plannedFinishDate', e.target.value)}
                                min={task.plannedStartDate ? task.plannedStartDate.toISOString().split('T')[0] : getMinDate(index, 'plannedFinishDate')}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!isFormValid || isLoading}
              onClick={handleSubmit}
            >
              {isLoading ? 'Saving...' : 'Save Assignment & Schedule'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};



/// Projact manager Assign
const ProjectManagerAssignmentPopup = ({ isOpen, onClose, onAssign, isLoading }) => {
  const [projectManager, setProjectManager] = useState('');

  // Options array for designers
  const options = [
    { id: "0CStJvlHW1fe8xnxJdKpWm9Hkck1", name: "Project Manager" },
      { id: "PV4tQohnD4g3kvaL4it8UFqQ7uW2", name: "Vijay Singh" }
    // { id: "qvzMCFUePDbsVJm8udl7mzjlP7x1", name: "Sona Bakale"},
    // { id: "heOAz54UbmUsaFJPYeryGRThZkA2", name: "Sanjay Kumar"}
  ];

  if (!isOpen) return null;

  const handleDesignerChange = (e) => {
    setProjectManager(e.target.value);
  };

  const handleSubmit = () => {
    const designer = options.find(opt => opt.id === projectManager);
    if (designer) {
      onAssign({
        designerId: designer.id,
        designerName: designer.name
      });
      setProjectManager('');
    }
  };

  return (
    <div className="popup-overlay">
      <div className="popup-content">
        <div className="popup-header">
          <h3 className="popup-title">Select Execution Head</h3>
          <button className="close-btn" onClick={onClose}><X size={20} /></button>
        </div>

        <div>
          {/* <label className="input-label">Select Designer</label> */}
          <select
            className="popup-select"
            value={projectManager}
            onChange={handleDesignerChange}
          >
            <option value="">Select a Project  Manager...</option>
            {options.map(designer => (
              <option key={designer.id} value={designer.id}>
                {designer.name}
              </option>
            ))}
          </select>
        </div>

        <button
          className="popup-submit-btn"
          disabled={!projectManager || isLoading}
          onClick={handleSubmit}
        >
          {isLoading ? 'Assigning...' : 'Assign Project Manager'}
        </button>
      </div>
    </div>
  );
};

const ExecutionTeamTaskAssignmentPopup = ({ isOpen, onClose, onAssignTeamTask, isLoading, projectManagerData }) => {
  // Team selection state
  const [selectedTeam, setSelectedTeam] = useState({
    projectManager: '',
    executionHead: ''
  });



  // Task scheduling state - keeping your existing tasks array
  const [executionTask, setExecutionTask] = useState([
    { name: 'Site Work (electrical, plumbing, etc)', role: 'executionHead', order: 260, phase: 'execution', plannedStartDate: null, plannedFinishDate: null, actualStartDate: null, actualFinishDate: null, stageName: 'Civil Work', stageNo: 1, stagePercentage: 20, folders: [{ id: "mom", displayName: 'MOM' }, { id: "presentation-renders", displayName: '3D Presentation (PDF)' }], subtasks: [] },
    { name: 'Production Completed', role: 'executionHead', order: 270, phase: 'execution', plannedStartDate: null, plannedFinishDate: null, stageName: 'Factory Work', stageNo: 1, stagePercentage: 40, folders: [{ id: 'mom', displayName: 'MOM' }, { id: 'presentation-renders', displayName: '3D Presentation (PDF)' }], subtasks: [] },
    { name: '40% Collection', role: 'executionHead', order: 280, phase: 'execution', plannedStartDate: null, plannedFinishDate: null, stageName: 'Kick Start Modular Work', stageNo: 2, stagePercentage: 43, folders: [{ id: 'mom', displayName: 'MOM' }, { id: 'presentation-renders', displayName: 'Material Images (PDF)' }], subtasks: [] },
    { name: 'Material Dispatch', role: 'executionHead', order: 290, phase: 'execution', plannedStartDate: null, plannedFinishDate: null, stageName: 'Kick Start Modular Work', stageNo: 1, stagePercentage: 45, folders: [{ id: 'mom', displayName: 'MOM' }, { id: 'presentation-renders', displayName: '3D Presentation (PDF)' }], subtasks: [] },
    { name: 'Modular Work (DPR)', role: 'projectManager', order: 300, phase: 'execution', plannedStartDate: null, plannedFinishDate: null, stageName: 'Modular Work', stageNo: 1, stagePercentage: 85, folders: [{ id: 'mom', displayName: 'MOM' }, { id: 'presentation-renders', displayName: '3D Presentation (PDF)' }], subtasks: [] },
    { name: 'On Site QC', role: 'projectManager', order: 310, phase: 'execution', plannedStartDate: null, plannedFinishDate: null, stageName: 'Final QC', stageNo: 1, stagePercentage: 88, folders: [{ id: 'mom', displayName: 'MOM' }, { id: 'presentation-renders', displayName: '3D Presentation (PDF)' }], subtasks: [] },
    { name: 'Final BOQ', role: 'projectManager', order: 320, phase: 'execution', plannedStartDate: null, plannedFinishDate: null, stageName: 'Final QC', stageNo: 1, stagePercentage: 91, folders: [{ id: 'mom', displayName: 'MOM' }, { id: 'presentation-renders', displayName: '3D Presentation (PDF)' }], subtasks: [] },
    { name: 'Collect PDC', role: 'projectManager', order: 330, phase: 'execution', plannedStartDate: null, plannedFinishDate: null, stageName: 'Final QC', stageNo: 1, stagePercentage: 95, folders: [{ id: 'mom', displayName: 'MOM' }, { id: 'presentation-renders', displayName: '3D Presentation (PDF)' }], subtasks: [] },
    { name: 'Deep Cleaning', role: 'projectManager', order: 340, phase: 'execution', plannedStartDate: null, plannedFinishDate: null, stageName: 'Handover', stageNo: 1, stagePercentage: 97, folders: [{ id: 'mom', displayName: 'MOM' }, { id: 'presentation-renders', displayName: '3D Presentation (PDF)' }], subtasks: [] },
    { name: 'Handover', role: 'projectManager', order: 350, phase: 'execution', plannedStartDate: null, plannedFinishDate: null, stageName: 'Handover', stageNo: 1, stagePercentage: 99, folders: [{ id: 'mom', displayName: 'MOM' }, { id: 'presentation-renders', displayName: '3D Presentation (PDF)' }], subtasks: [] },
    { name: 'Warranty Document Handover', role: 'projectManager', order: 360, phase: 'execution', plannedStartDate: null, plannedFinishDate: null, stageName: 'Handover', stageNo: 1, stagePercentage: 100, folders: [{ id: 'mom', displayName: 'MOM' }, { id: 'presentation-renders', displayName: '3D Presentation (PDF)' }], subtasks: [] },

  ]);

  // Options arrays for different roles
  const options = {
    projectManager: [
      { id: "qFPBerhnQURjzs9MsH3KuJmiazp2", name: "Project Manager" },
    ],
    executionHead: [
      { id: "PV4tQohnD4g3kvaL4it8UFqQ7uW2", name: "Vijay Singh" }
    ]
  };

  if (!isOpen) return null;

  const handleTeamChange = (role, value) => {
    setSelectedTeam(prev => ({
      ...prev,
      [role]: value
    }));
  };

  const handleTaskDateChange = (index, field, dateValue) => {
    setExecutionTask(prevTasks => {
      const updatedTasks = [...prevTasks];
      const date = dateValue ? new Date(dateValue) : null;
      updatedTasks[index] = {
        ...updatedTasks[index],
        [field]: date
      };
      return updatedTasks;
    });
  };

  const handleSubmit = () => {
    const teamData = {
      projectManager: options.projectManager.find(opt => opt.id === selectedTeam.projectManager)
    };

    // Build assignments object, only including juniorDesigner if selected
    const assignments = {
      projectManager: {
        uid: projectManagerData?.uid,
        displayName: projectManagerData?.displayName
      },
      executionHead: {
        uid: window.localStorage.getItem('projectUserUID'),
        displayName: window.localStorage.getItem('projectUserDisplayName')

      }
    };




    // Extract specific dates
    const firstTaskStartDate = executionTask.length > 0 ? executionTask[0].plannedStartDate : null;
    const lastTaskFinishDate = executionTask.length > 0 ? executionTask[executionTask.length - 1].plannedFinishDate : null;
    const thirdTaskStartDate = executionTask.length >= 3 ? executionTask[2].plannedStartDate : null; // Index 5 for 6th task just get 40% collection start date



    onAssignTeamTask({
      assignments,
      tasks: executionTask,
      username: userName || 'system',
      firstTaskStartDate,
      lastTaskFinishDate,
      thirdTaskStartDate
    });

    // Reset form
    setSelectedTeam({
      projectManager: '',
      executionHead: ''
    });
  };

  // Updated validation - junior designer is now optional
  const isTeamValid =
    selectedTeam.projectManager &&
    selectedTeam.executionHead;
  // Note: juniorDesigner removed from validation

  const areTasksValid = executionTask.every(task => task.plannedStartDate && task.plannedFinishDate);
  const isFormValid = areTasksValid;

  return (
    <div className="modal" style={{ display: isOpen ? 'block' : 'none', backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1050 }}>
      <div className="modal-dialog modal-xl modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Project Assignment and Scheduling</h5>
            <button type="button" className="close" onClick={onClose}>
              <span>&times;</span>
            </button>
          </div>

          <div className="modal-body">
            <div className="container-fluid">
              <div className="row">
                {/* Left Column - Team Assignment */}
                <div className="col-4 border-right">
                  <h6 className="mb-3">Team Assignment</h6>
                  <form>
                    <div className="form-group mb-3">
                      <label className="form-label">Project Manager <span className="text-danger">*</span></label>
                      {/* <select
                        className="form-control form-control-sm"
                        value={selectedTeam.designer}
                        onChange={(e) => handleTeamChange('designer', e.target.value)}
                      >
                        <option value="">Select designer...</option>
                        {options.designers.map(person => (
                          <option key={person.id} value={person.id}>
                            {person.name}
                          </option>
                        ))}
                      </select> */}
                      <input value={projectManagerData.displayName} className='form-control form-control-sm' disabled />
                    </div>


                  </form>
                </div>

                {/* Right Column - Task Scheduling */}
                <div className="col-8">
                  <h6 className="mb-3">Task Timeline</h6>
                  <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <table className="table table-sm table-striped">
                      <thead>
                        <tr>
                          <th>Task</th>
                          <th>Start Date</th>
                          <th>End Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {executionTask.map((task, index) => (
                          <tr key={index}>
                            <td>{task.name}</td>
                            <td>
                              <input
                                type="date"
                                className="form-control form-control-sm"
                                value={task.plannedStartDate ? task.plannedStartDate.toISOString().split('T')[0] : ''}
                                onChange={(e) => handleTaskDateChange(index, 'plannedStartDate', e.target.value)}
                              />
                            </td>
                            <td>
                              <input
                                type="date"
                                className="form-control form-control-sm"
                                value={task.plannedFinishDate ? task.plannedFinishDate.toISOString().split('T')[0] : ''}
                                onChange={(e) => handleTaskDateChange(index, 'plannedFinishDate', e.target.value)}
                                min={task.plannedStartDate ? task.plannedStartDate.toISOString().split('T')[0] : ''}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!isFormValid || isLoading}
              onClick={handleSubmit}
            >
              {isLoading ? 'Saving...' : 'Save Assignment & Schedule'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const SubtaskCreationPopup = ({ isOpen, onClose, onSubmit, isLoading }) => {
  const [subtaskName, setSubtaskName] = useState('');
  const [selectedAssignee, setSelectedAssignee] = useState('');

  // Team members options - same as used in other popups
  const teamOptions = [
    { id: "YUpQaAF77mO8hUmC5dgRK0wFCif2", name: "Akshata Patil" },
    { id: "FGbfZtqsj3OVq3yTsOwWml5NBRA3", name: "Akhil Josekutty" },
    { id: "DGZ6g1MDpUgo3tH7WK8vgw6PAwr1", name: "Chaitanya V" },
    { id: "qFPBerhnQURjzs9MsH3KuJmiazp2", name: "Prithi Sridharan" },
    { id: "lFUimxY44cXpJwimtFh1r4G6D3s2", name: "Preeti Niranjan" },
    { id: "iKp7E8gn4SQXXuF9j0Y8BK4TAff1", name: "Shalini" },
    { id: "7k9tFxIkAuhIGINJ4awNeMzm4h23", name: "Ibrahim" },
    { id: "SLRt0LwJqGVnYQ0ublia5NMWIQ12", name: "Afshan Hussain" },
    { id: "BgnvKVeuMugTr2Mz6auE5YihShM2", name: "Vinitha" },
    { id: "evbQlJ9q2fT8T66jFpOLqXapLp53", name: "Rahul.P" },
    { id: "PocepGixlLZ9vJCxOcMEzKpxyLF2", name: "Reshma R" },
    { id: "ULvyYnnIDeTajBAeg7PRe3ieE6x2", name: "Ashith N" },
    { id: "PV4tQohnD4g3kvaL4it8UFqQ7uW2", name: "Vijay Singh" }
  ];

  if (!isOpen) return null;

  const handleSubmit = () => {
    const assignee = teamOptions.find(opt => opt.id === selectedAssignee);
    if (subtaskName.trim() && assignee) {
      onSubmit({
        name: subtaskName.trim(),
        assignedTo: assignee.id,
        assignedToName: assignee.name
      });
      setSubtaskName('');
      setSelectedAssignee('');
    }
  };

  return (
    <div className="modal show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Create Subtask</h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label="Close"
              disabled={isLoading}
            />
          </div>

          <div className="modal-body">
            <div className="mb-3">
              <label className="form-label">Subtask Name <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-control"
                value={subtaskName}
                onChange={(e) => setSubtaskName(e.target.value)}
                placeholder="Enter subtask name"
                disabled={isLoading}
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Assign To <span className="text-danger">*</span></label>
              <select
                className="form-select"
                value={selectedAssignee}
                onChange={(e) => setSelectedAssignee(e.target.value)}
                disabled={isLoading}
              >
                <option value="">Select team member...</option>
                {teamOptions.map(member => (
                  <option key={member.id} value={member.id}>
                    {member.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={!subtaskName.trim() || !selectedAssignee || isLoading}
            >
              {isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Creating...
                </>
              ) : (
                'Create Subtask'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


const SubTaskItem = ({ subtask, onUpdate, onDelete, onStart, folders, parentTaskId, projectId }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(subtask.name);
  const [isUpdatePopupOpen, setIsUpdatePopupOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const { mutations } = useProjectTasks(projectId);

  const handleEdit = () => {
    onUpdate({ projectId, taskId: parentTaskId, subtaskId: subtask.id, name: editedName });
    setIsEditing(false);
  };

  const handleUpdate = (updateData) => {
    onUpdate({
      projectId,
      taskId: parentTaskId,
      subtaskId: subtask.id,
      description: updateData.description,
      username: localStorage.getItem('projectUserDisplayName'),
      folder: updateData.folder,
      files: updateData.files
    });
  };

  const handleStart = () => {
    onStart({
      projectId,
      taskId: parentTaskId,
      subtaskId: subtask.id,
      username: localStorage.getItem('projectUserDisplayName')
    });
  };

  const handleCheckboxChange = () => {
    if (!subtask.completed && subtask.start) {
      onUpdate({
        projectId,
        taskId: parentTaskId,
        subtaskId: subtask.id,
        completed: true,
        username: localStorage.getItem('projectUserDisplayName'),
      });
    }
  };

  return (
    <div className="subtask-wrapper">
      <div className="subtask-item">
        <div className="subtask-main">
          <div className="subtask-content">
            <div className="checkbox-wrapper">
              <input
                type="checkbox"
                checked={subtask.completed}
                onChange={handleCheckboxChange}
                className={`subtask-checkbox ${subtask.completed ? 'completed' : ''}`}
                disabled={subtask.completed || !subtask.start || mutations.updateSubtask.isLoading}
              />
              {subtask.completed && <Lock size={12} className="lock-icon" />}
            </div>
            {isEditing ? (
              <div className="subtask-form">
                <input
                  type="text"
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  className="subtask-input"
                />
                <button
                  onClick={handleEdit}
                  className="btn btn-update"
                  disabled={mutations.updateSubtask.isLoading}
                >
                  Save
                </button>
                <button
                  onClick={() => setIsEditing(false)}
                  className="btn"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <span className={`subtask-name ${subtask.completed ? 'completed' : ''}`}>
                {subtask.name}
              </span>
            )}
            {subtask.assignedTo && (
              <span className="subtask-name text-muted">
                ({subtask.assignedToName})
              </span>
            )}
            {subtask.start && (
              <span className="subtask-name text-success">
                Start: {new Date(subtask.start).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </span>
            )}
            {subtask.completed && (
              <span className="subtask-name text-danger">
                End: {new Date(subtask.updatedAt).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })} &nbsp;&nbsp; Dur: {
                  Math.round((new Date(subtask.updatedAt) - new Date(subtask.start)) / (1000 * 3600 * 24)) + 1
                }
              </span>
            )}
          </div>

          <div className="subtask-actions">
            {!subtask.start && !subtask.completed && (
              <button
                onClick={handleStart}
                className="btn btn-primary btn-sm"
                disabled={mutations.startSubtask?.isLoading}
              >
                Start
              </button>
            )}
            {subtask.start && !subtask.completed && (
              <>
                <button
                  onClick={() => setIsEditing(true)}
                  className="subtask-action-btn"
                  title="Edit"
                  disabled={mutations.updateSubtask.isLoading}
                >
                  <Edit2 size={16} />
                </button>
                <button
                  onClick={() => setIsUpdatePopupOpen(true)}
                  className="subtask-action-btn update"
                  title="Update"
                  disabled={mutations.updateSubtask.isLoading}
                >
                  Update
                </button>
                <button
                  onClick={() => onDelete({ projectId, taskId: parentTaskId, subtaskId: subtask.id })}
                  className="subtask-action-btn delete"
                  title="Delete"
                  disabled={mutations.deleteSubtask.isLoading}
                >
                  <Trash2 size={16} />
                </button>
              </>
            )}
            {subtask.descriptions && subtask.descriptions.length > 0 && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="subtask-action-btn toggle"
                title="Toggle Updates"
              >
                {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
            )}
          </div>
        </div>
      </div>

      {isExpanded && subtask.descriptions && subtask.descriptions.length > 0 && (
        <div className="subtask-updates-dropdown">
          <div className="updates-list">
            {subtask.descriptions.map((desc, index) => (
              <div key={index} className="update-item">
                <div className="update-timestamp">
                  Update {index + 1} - {new Date(desc.timestamp).toLocaleString()}
                </div>
                <p className="update-text">{desc.text}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <TaskUpdatePopup
        isOpen={isUpdatePopupOpen}
        onClose={() => setIsUpdatePopupOpen(false)}
        onSubmit={(updatedData) => {
          handleUpdate(updatedData);
          setIsUpdatePopupOpen(false);
        }}
        projectId={projectId}
        initialData={{ name: subtask.name, description: '' }}
        availableFolders={folders}
        title="Update Subtask"
      />
    </div>
  );
};

// Complete TaskItem component with QC assignment fix

const TaskItem = ({
  task,
  taskUpdatedDur,
  currentUserUid,
  currentUserRole,
  isActive,
  projectId,
  isLastTask,
  hasTaskTeamDefined,
  onLastTaskComplete,
  onQuickMeeting,
  onExecutionHead,
  previousTaskCompletionDate,
  isFirstTask,
  taskIndex,
  previousTaskData
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [newSubtaskName, setNewSubtaskName] = useState('');
  const [isCompletionConfirmationOpen, setIsCompletionConfirmationOpen] = useState(false);
  const [isQuickMeetingConfirmationOpen, setIsQuickMeetingConfirmationOpen] = useState(false);
  const [isLastTaskConfirmationOpen, setIsLastTaskConfirmationOpen] = useState(false);
  const [isProjectManagerAssignmentOpen, setIsProjectManagerAssignmentOpen] = useState(false);
  const [isSubtaskPopupOpen, setIsSubtaskPopupOpen] = useState(false);

  const canUpdate = task.role === currentUserRole;
  
  const {
    updateTask,
    completeTask,
    updateSubtask,
    addSubtask,
    deleteSubtask,
    startTask,
    startSubtask,
    mutations
  } = useProjectTasks(projectId);

  const areAllSubtasksComplete = task.subtasks.length > 0
    ? task.subtasks.every(subtask => subtask.completed)
    : true;

  // Calculate estimated dates (keeping your existing logic)
  const getEstimatedDates = () => {
    let taskDuration;

    if (taskUpdatedDur && taskUpdatedDur > 0) {
      taskDuration = taskUpdatedDur;
    } else if (task.plannedStartDate && task.plannedFinishDate) {
      taskDuration = Math.round((new Date(task.plannedFinishDate) - new Date(task.plannedStartDate)) / (1000 * 3600 * 24)) + 1;
    } else {
      taskDuration = 0;
    }

    let estimatedStartDate;
    let estimatedFinishDate;

    if (isFirstTask) {
      if (task.start) {
        estimatedStartDate = new Date(task.start);
      } else if (task.plannedStartDate) {
        estimatedStartDate = new Date(task.plannedStartDate);
      } else {
        estimatedStartDate = new Date();
      }
    } else {
      if (taskUpdatedDur) {
        estimatedStartDate = new Date(task.plannedStartDate);
        estimatedStartDate.setDate(estimatedStartDate.getDate() + taskUpdatedDur);

        if (taskIndex === 1 && previousTaskData) {
          const prevPlannedStart = previousTaskData.plannedStartDate;
          const prevActualStart = previousTaskData.start;

          if (prevPlannedStart && prevActualStart) {
            const timeDifference = new Date(prevActualStart) - new Date(prevPlannedStart);
            estimatedStartDate = new Date(estimatedStartDate.getTime() + timeDifference);
          }
        }
      } else {
        estimatedStartDate = task.plannedStartDate ? new Date(task.plannedStartDate) : new Date();
      }
    }

    estimatedFinishDate = new Date(task.plannedFinishDate);
    if (isActive) {
      estimatedFinishDate = new Date(task.start);
      estimatedFinishDate.setDate(estimatedFinishDate.getDate() + ((new Date(task.plannedFinishDate) - new Date(task.plannedStartDate)) / (1000 * 3600 * 24)));
    } else if (task.start) {
      estimatedFinishDate.setDate(estimatedFinishDate.getDate() + taskDuration);
    } else {
      estimatedFinishDate.setDate(estimatedFinishDate.getDate() + taskDuration);
    }

    return {
      estimatedStartDate,
      estimatedFinishDate,
      taskDuration
    };
  };

  const { estimatedStartDate, estimatedFinishDate, taskDuration } = getEstimatedDates();

  const handleStartTask = () => {
    if (task.name === 'Site Validation') {
      setIsProjectManagerAssignmentOpen(true);
      return;
    }

    startTask({
      projectId,
      taskId: task.taskId,
      username: localStorage.getItem('projectUserDisplayName'),
      timestamp: new Date().toISOString()
    });
  };

  const handleProjectManagerAssign = ({ designerId, designerName }) => {
    startTask({
      projectId,
      taskId: task.taskId,
      username: localStorage.getItem('projectUserDisplayName'),
      timestamp: new Date().toISOString(),
      projectManagerId: designerId,
      projectManagerName: designerName
    });
     completeTask({ projectId, taskId: task.taskId });
    setIsProjectManagerAssignmentOpen(false);
  };

  // UPDATED: handleUpdateTask to properly handle QC assignee
  const handleUpdateTask = (updatedTask) => {
    const updateData = {
      projectId,
      taskId: task.taskId,
      description: updatedTask.description,
      username: localStorage.getItem('projectUserDisplayName'),
      qualityCheck: updatedTask.qualityCheck,
      folder: updatedTask.folder,
      file: updatedTask.files
    };

    // Add QC assignee if provided
    if (updatedTask.qcAssignee) {
      updateData.qcAssignee = updatedTask.qcAssignee;
    }

    console.log("Task update payload:", updateData);
    updateTask(updateData);
  };

  const handleCompleteTask = () => {

      // Check if task is "Validation 2D drawings"
  if (task.name === 'Validation 2D drawings') {
    // setIsPMHeadAssignmentOpen(true);
    setIsProjectManagerAssignmentOpen(true);
    return;
  }

    if (isLastTask && areAllSubtasksComplete) {
      if (localStorage.getItem('projectUserRole') === 'designer') {
        onQuickMeeting(task.taskId);
      } else if (localStorage.getItem('projectUserRole') === 'designer') {
        onExecutionHead(task.taskId);
      } else {
        onLastTaskComplete(task.taskId);
      }
    } else {
      setIsCompletionConfirmationOpen(true);
    }
  };


  const handleConfirmComplete = () => {
    completeTask({ projectId, taskId: task.taskId });
    setIsCompletionConfirmationOpen(false);
  };

  const handleAssignDesignHead = () => {
    completeTask({ projectId, taskId: task.taskId });
    setIsLastTaskConfirmationOpen(false);
  };

  const handleAssignDesignTeamTask = () => {
    completeTask({ projectId, taskId: task.taskId });
    setIsQuickMeetingConfirmationOpen(false);
  };

  const handleAddSubtask = (subtaskData) => {
    addSubtask({
      projectId,
      taskId: task.taskId,
      name: subtaskData.name,
      assignedTo: subtaskData.assignedTo,
      assignedToName: subtaskData.assignedToName,
      username: localStorage.getItem('projectUserDisplayName')
    });
  };

  const handleStartSubtask = (subtaskData) => {
    startSubtask({
      projectId,
      taskId: subtaskData.taskId,
      subtaskId: subtaskData.subtaskId,
      username: subtaskData.username
    });
  };

  // Add a new handler for PM Head assignment
const handlePMHeadAssign = ({ designerId, designerName }) => {
  completeTask({ 
    projectId, 
    taskId: task.taskId,
    pmHeadId: designerId,
    pmHeadName: designerName
  });
  // setIsPMHeadAssignmentOpen(false);
};


  return (
    <div className="task-item">
      <div className="task-header">
        <div
          className={`task-content ${task.completed ? 'completed' : isActive ? 'active' : ''}`}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="task-container row align-items-center">
            <div className="col">
              <span className="task-name">{task.name}</span>
            </div>
          </div>
          <div className='row justify-content-end'>
            <div className="col-auto">
              {task.plannedStartDate && (
                <span className="task-name task-detail text-success" data-bs-toggle="tooltip" data-bs-placement="top" title="Planned Start Date">
                  PS: {new Date(task.plannedStartDate).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })} <br />
                </span>
              )}

              {!task.start && !task.completed && task.plannedStartDate && (
                <span className="task-name task-detail text-info" data-bs-toggle="tooltip" data-bs-placement="top" title="Estimated Start Date">
                  ES: {estimatedStartDate.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })} <br />
                </span>
              )}

              {task.start && (
                <span className="task-name task-detail text-success" data-bs-toggle="tooltip" data-bs-placement="top" title="Actual Start Date">
                  AS: {new Date(task.start).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })} <br />
                </span>
              )}
            </div>

            <div className="col-auto">
              {task.plannedFinishDate && (
                <span className="task-name task-detail text-success" data-bs-toggle="tooltip" data-bs-placement="top" title="Planned Finish Date">
                  PF: {new Date(task.plannedFinishDate).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}<br />
                </span>
              )}

              {!task.completed && task.plannedFinishDate && (
                <span className="task-name task-detail text-info" data-bs-toggle="tooltip" data-bs-placement="top" title="Estimated Finish Date">
                  EF: {estimatedFinishDate.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}<br />
                </span>
              )}

              {task.completed && (
                <span className="task-detail task-name text-danger" data-bs-toggle="tooltip" data-bs-placement="top" title="Actual Finish Date">
                  AF: {new Date(task.updatedAt).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })} <br />
                </span>
              )}
            </div>

            <div className="col-auto">
              {task.plannedStartDate && task.plannedFinishDate && (
                <span className="task-detail task-name text-warning" data-bs-toggle="tooltip" data-bs-placement="top" title="Planned Duration">
                  PD: {Math.round((new Date(task.plannedFinishDate) - new Date(task.plannedStartDate)) / (1000 * 3600 * 24)) + 1}<br />
                </span>
              )}

              {task.completed && task.start && (
                <span className="task-detail task-name text-danger" data-bs-toggle="tooltip" data-bs-placement="top" title="Actual Duration">
                  AD: {Math.round((new Date(task.updatedAt) - new Date(task.start)) / (1000 * 3600 * 24)) + 1}
                </span>
              )}
            </div>

            <div className='col-auto'>
              {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </div>
          </div>
        </div>

        {canUpdate && !task.start && !task.completed && (
          <button
            className="btn btn-primary btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              handleStartTask();
            }}
            disabled={mutations.startTask?.isLoading}
          >
            Start
          </button>
        )}
        {canUpdate && task.start && !task.completed && (
          <div className="task-buttons">
            <button
              className="btn btn-update"
              onClick={() => setIsPopupOpen(true)}
              disabled={mutations.updateTask.isLoading}
            >
              Update
            </button>
            {areAllSubtasksComplete && (
              <button
                className="btn btn-complete"
                onClick={handleCompleteTask}
                disabled={mutations.completeTask.isLoading}
              >
                {mutations.completeTask.isLoading ? 'Completing...' : 'Complete'}
              </button>
            )}
          </div>
        )}
      </div>

      {isExpanded && (
        <div className="task-details-container">
          {task.descriptions && task.descriptions.length > 0 && (
            <div className="description-container">
              <h4 className="updates-header">Task Updates</h4>
              {task.descriptions.map((desc, index) => (
                <div key={index} className="description-item">
                  <div className="description-timestamp">
                    Update {index + 1} - {new Date(desc.timestamp).toLocaleString()}
                  </div>
                  <p className="description-text">{desc.text}</p>
                </div>
              ))}
            </div>
          )}

          <div className="subtask-container">
            <h4 className="subtask-header">Subtasks</h4>
            {task.subtasks.map(subtask => (
              <SubTaskItem
                key={subtask.id}
                subtask={subtask}
                parentTaskId={task.taskId}
                projectId={projectId}
                folders={task.folders}
                onUpdate={updateSubtask}
                onDelete={deleteSubtask}
                onStart={handleStartSubtask}
              />
            ))}
            {!task.completed && (
              <button
                onClick={() => setIsSubtaskPopupOpen(true)}
                className="btn btn-update"
                disabled={mutations.addSubtask.isLoading}
              >
                <Plus size={16} className="me-1" />
                Add Subtask
              </button>
            )}
          </div>
        </div>
      )}

      {/* CRITICAL FIX: Pass taskName prop */}
      <TaskUpdatePopup
        isOpen={isPopupOpen}
        onClose={() => setIsPopupOpen(false)}
        onSubmit={(updatedTask) => {
          handleUpdateTask(updatedTask);
          setIsPopupOpen(false);
        }}
        projectId={projectId}
        initialData={{ name: task.name, description: '' }}
        availableFolders={task.folders}
        taskName={task.name} // THIS IS THE CRITICAL LINE
        title={`Update Task: ${task.name}`}
      />

      <TaskCompletionConfirmationPopup
        isOpen={isCompletionConfirmationOpen}
        onClose={() => setIsCompletionConfirmationOpen(false)}
        onConfirm={handleConfirmComplete}
        taskName={task.name}
        isLoading={mutations.completeTask.isLoading}
        taskType="task"
      />

      <ProjectManagerAssignmentPopup
        isOpen={isProjectManagerAssignmentOpen}
        onClose={() => setIsProjectManagerAssignmentOpen(false)}
        onAssign={handleProjectManagerAssign}
        isLoading={mutations.startTask?.isLoading}
      />

      <SubtaskCreationPopup
        isOpen={isSubtaskPopupOpen}
        onClose={() => setIsSubtaskPopupOpen(false)}
        onSubmit={(subtaskData) => {
          handleAddSubtask(subtaskData);
          setIsSubtaskPopupOpen(false);
        }}
        isLoading={mutations.addSubtask.isLoading}
      />

       {/* <ProjectManagerAssignmentPopup
          isOpen={isPMHeadAssignmentOpen}
          onClose={() => setIsPMHeadAssignmentOpen(false)}
          onAssign={handlePMHeadAssign}
          isLoading={mutations.completeTask?.isLoading}
          title="Assign Execution Head" 
        /> */}
    </div>
  );
};

const StageAccordion = ({ 
  title, 
  tasks, 
  taskUpdatedDur, 
  stageColor, 
  projectId, 
  currentUserUid, 
  currentUserRole, 
  activeTaskId, 
  hasTaskTeamDefined, 
  onLastTaskComplete, 
  onQuickMeeting, 
  onExecutionHead, 
  completedStagePercentage, 
  currentActiveStage, 
  projectStatus 
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  // FIXED: Calculate cascading estimates for all tasks in this stage
  const tasksWithEstimates = useMemo(() => {
    if (!tasks || tasks.length === 0) return [];

    // Build array iteratively to avoid self-reference
    const result = [];

    for (let index = 0; index < tasks.length; index++) {
      const task = tasks[index];
      const isFirstTask = index === 0;
      const previousTask = index > 0 ? result[index - 1] : null; // Use result array instead
      
      // Calculate this task's duration
      let taskDuration = 0;
      if (task.plannedStartDate && task.plannedFinishDate) {
        taskDuration = getBusinessDaysBetween(
          new Date(task.plannedStartDate),
          new Date(task.plannedFinishDate)
        );
      }

      let estimatedFinishDate;
      let estimatedStartDate;

      // If task is completed
      if (task.completed) {
        estimatedFinishDate = new Date(task.updatedAt);
        estimatedStartDate = new Date(task.start);
      }
      // If task has started but not completed
      else if (task.start) {
        const startDate = new Date(task.start);
        estimatedStartDate = startDate;
        estimatedFinishDate = taskDuration > 0 
          ? addBusinessDays(startDate, taskDuration - 1)
          : startDate;
      }
      // If task hasn't started yet
      else {
        if (isFirstTask) {
          // First task uses planned start or today
          estimatedStartDate = task.plannedStartDate 
            ? new Date(task.plannedStartDate)
            : new Date();
          
          if (!isWorkingDay(estimatedStartDate)) {
            estimatedStartDate = getNextWorkingDay(estimatedStartDate);
          }
        } else if (previousTask) {
          // Get previous task's estimated finish from result array
          if (previousTask.estimatedFinishDate) {
            // Start next business day after previous task finishes
            estimatedStartDate = getNextWorkingDay(
              new Date(previousTask.estimatedFinishDate)
            );
          } else {
            // Fallback to planned dates
            estimatedStartDate = task.plannedStartDate 
              ? new Date(task.plannedStartDate)
              : new Date();
            
            if (!isWorkingDay(estimatedStartDate)) {
              estimatedStartDate = getNextWorkingDay(estimatedStartDate);
            }
          }
        } else {
          // Fallback
          estimatedStartDate = task.plannedStartDate 
            ? new Date(task.plannedStartDate)
            : new Date();
        }

        // Calculate estimated finish
        estimatedFinishDate = taskDuration > 0 
          ? addBusinessDays(estimatedStartDate, taskDuration - 1)
          : estimatedStartDate;
      }

      // Calculate delay compared to plan
      let delayDays = 0;
      if (task.plannedStartDate && estimatedStartDate) {
        const plannedStart = new Date(task.plannedStartDate);
        plannedStart.setHours(0, 0, 0, 0);
        const estStart = new Date(estimatedStartDate);
        estStart.setHours(0, 0, 0, 0);
        
        if (estStart > plannedStart) {
          delayDays = getBusinessDaysBetween(plannedStart, estStart) - 1;
        } else if (estStart < plannedStart) {
          delayDays = -(getBusinessDaysBetween(estStart, plannedStart) - 1);
        }
      }

      // Add to result array
      result.push({
        ...task,
        estimatedFinishDate,
        estimatedStartDate,
        taskDuration,
        delayDays
      });
    }

    return result;
  }, [tasks]);

  const getStageStyle = () => {
    return {
      borderLeft: `5px solid ${stageColor}`,
      backgroundColor: '#e6e6e6'
    };
  };

  return (
    <div className="stage-accordion mb-4">
      <div
        className="stage-header p-3 rounded-t cursor-pointer d-flex justify-content-between align-items-center"
        style={getStageStyle()}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <h3 className="text-lg font-medium">{title}</h3>

        <div className="d-flex align-items-center" style={{ width: '50%' }}>
          {title === 'Design Phase' && completedStagePercentage && projectStatus === 'design-phase' ? (
            <div className="flex-grow-1 me-3">
              <ProgressBar
                now={completedStagePercentage}
                label={<span className="text-black">{`${completedStagePercentage}%`}</span>}
                className="custom-progress"
                style={{ backgroundColor: '#F9FAFB', height: '25px', borderRadius: '25px' }}
              />
            </div>
          ) : (
            <div className="flex-grow-1"></div>
          )}
          <div>
            {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="stage-content border border-gray-200 rounded-b">
          {tasksWithEstimates.length > 0 ? (
            tasksWithEstimates.map((task, index) => {
              const previousTask = index > 0 ? tasksWithEstimates[index - 1] : null;
              const previousTaskEstimatedFinish = previousTask?.estimatedFinishDate;
              const previousTaskCompletionDate = previousTask?.completed 
                ? previousTask.updatedAt 
                : null;

              return (
                <TaskItem
                  key={task.taskId}
                  task={task}
                  taskUpdatedDur={taskUpdatedDur}
                  currentUserUid={currentUserUid}
                  currentUserRole={currentUserRole}
                  isActive={task.taskId === activeTaskId}
                  projectId={projectId}
                  isLastTask={index === tasksWithEstimates.length - 1}
                  isFirstTask={index === 0}
                  taskIndex={index}
                  hasTaskTeamDefined={hasTaskTeamDefined}
                  onLastTaskComplete={onLastTaskComplete}
                  onQuickMeeting={onQuickMeeting}
                  onExecutionHead={onExecutionHead}
                  previousTaskData={previousTask}
                  previousTaskCompletionDate={previousTaskCompletionDate}
                  previousTaskEstimatedFinish={previousTaskEstimatedFinish}
                />
              );
            })
          ) : (
            <div className="p-4 text-gray-500 text-center">No tasks in this stage</div>
          )}
        </div>
      )}
    </div>
  );
};

const SalesTaskList = () => {
  const { projectId } = useParams();
  const [isDesignerAssignmentOpen, setIsDesignerAssignmentOpen] = useState(false);
  const [isDesignerTeamAssignmentOpen, setIsDesignerTeamAssignmentOpen] = useState(false);
  const [isDesignerTeamTaskAssignmentOpen, setIsDesignerTeamTaskAssignmentOpen] = useState(false);
  const [isExecutionTeamAssignmentOpen, setIsExecutionTeamAssignmentOpen] = useState(false);
  const [isExecutionTeamTaskAssignmentOpen, setIsExecutionTeamTaskAssignmentOpen] = useState(false);
  const [taskIdFromPopup, setTaskIdFromPopup] = useState(null);
  const [taskFilter, setTaskFilter] = useState('all'); // New state for task filtering
  const { isLoading, isError, error, data } = useProjectTasks(projectId);
  const currentUserUid = window.localStorage.getItem('projectUserUID');
  const currentUserRole = window.localStorage.getItem('projectUserRole');

  const tasks = data ? data.data.tasks : [];
  const taskUpdatedDur = data ? data.data.updatedDuration : [];
  // console.log("tasks for console", taskUpdatedDate);
  const activeTaskId = data ? data.data.currentActiveTask : null;
  const projectStatus = data ? data.data.projectStatus : null;
  const hasTeamAssigned = data ? data.data.hasTeamAssigned : null;
  const hasExecutionTeamAssigned = data ? data.data.hasExecutionTeamAssigned : null;
  const hasTaskTeamDefined = data ? data.data.hasTaskTeamDefined : null;
  const currentActiveStage = data ? data.data.currentActiveStage : null;
  const completedStagePercentage = data ? data.data.completedStagePercentage : 0;
  const userName = window.localStorage.getItem('projectUserDisplayName');
  const { isLoading: isTasksLoading, isError: isTasksError, error: tasksError, data: tasksData } = useSingleProject(projectId);
  console.log("Project Data", tasksData);

  // Filter tasks based on selected filter
  const filteredTasks = useMemo(() => {
    if (!tasks) return [];

    switch (taskFilter) {
      case 'my-tasks':
        return tasks.filter(task => task.role === currentUserRole);
      case 'assigned-to-me':
        return tasks.filter(task => task.role === currentUserRole);
      case 'active-only':
        return tasks.filter(task => task.taskId === activeTaskId || task.start && !task.completed);
      case 'all':
      default:
        return tasks;
    }
  }, [tasks, taskFilter, currentUserUid, currentUserRole, activeTaskId]);

  // Define stages and their colors
  const stages = [
    {
      id: 'sales',
      title: 'Sales Phase',
      color: '#525252', // Green
      tasks: filteredTasks.filter(task => task.phase === 'sales' || !task.phase)  // Default to sales if no stage
    },
    {
      id: 'design',
      title: 'Design Phase',
      color: '#1164c2', // Blue
      tasks: filteredTasks.filter(task => task.phase === 'design')
    },
    {
      id: 'execution',
      title: 'Execution Phase',
      color: '#ff8903', // Orange
      tasks: filteredTasks.filter(task => task.phase === 'execution')
    }
  ];

  const {
    completeTask,
    assignDesigner,
    assignDesignerTeam,
    assignDesignerTeamTask,
    assignExecutionHead,
    assignExecutionTeamTask,
    mutations
  } = useProjectTasks(projectId);



  const handleDesignerAssignment = (designerData) => {
    assignDesigner({
      projectId: projectId,
      designerID: designerData.designerId,
      designerName: designerData.designerName,
      username: userName
    });
    completeTask({ projectId, taskId: taskIdFromPopup });
    setIsDesignerAssignmentOpen(false);
  };

  const handleExecutionHeadAssignment = (designerData) => {
    assignExecutionHead({
      projectId: projectId,
      executionHeadId: designerData.executionHeadId,
      executionHeadName: designerData.executionHeadName,
      username: userName
    });
    completeTask({ projectId, taskId: taskIdFromPopup });
    setIsExecutionTeamAssignmentOpen(false);
  };

  const handleDesignerTeamAssignment = (assignments) => {
    assignDesignerTeam({
      projectId: projectId,
      assignments: assignments,
      username: userName
    });
    setIsDesignerTeamAssignmentOpen(false);
  };

  const handleDesignerTeamTaskAssignment = (assignments) => {
    assignDesignerTeamTask({
      projectId: projectId,
      assignments: assignments.assignments,
      tasks: assignments.tasks,
      firstTaskStartDate: assignments.firstTaskStartDate,
      lastTaskFinishDate: assignments.lastTaskFinishDate,
      sixthTaskStartDate: assignments.sixthTaskStartDate,
      username: userName
    });
    completeTask({ projectId, taskId: taskIdFromPopup });
    setIsDesignerTeamTaskAssignmentOpen(false);
  };

  const handleExecutionTeamTaskAssignment = (assignments) => {
    assignExecutionTeamTask({
      projectId: projectId,
      assignments: assignments.assignments,
      tasks: assignments.tasks,
      firstTaskStartDate: assignments.firstTaskStartDate,
      lastTaskFinishDate: assignments.lastTaskFinishDate,
      thirdTaskStartDate: assignments.thirdTaskStartDate,
      username: userName
    });
    //  completeTask({ projectId, taskId: taskIdFromPopup });
    setIsExecutionTeamTaskAssignmentOpen(false);
  };

  // Automatically open designer assignment popup when all tasks are complete
  // useEffect(() => {
  //   if (tasks.length > 0 && tasks.every(task => task.completed) && projectStatus === 'sales-phase') {
  //     setIsDesignerAssignmentOpen(true);
  //   }
  // }, [tasks, projectStatus]);


  // Automatically open Team & Task assignment popup 
  // useEffect(() => {
  //   if (tasks.length > 0 && tasks.every(task => task.completed) && projectStatus === 'design-phase') {
  //     setIsDesignerTeamTaskAssignmentOpen(true);
  //   }
  // }, [tasks, projectStatus]);


  useEffect(() => {
    const currentUserRole = window.localStorage.getItem('projectUserRole');

    // Check if it's the initial page load
    if (
      projectStatus === 'design-phase' &&
      !hasTeamAssigned &&
      currentUserRole === 'designerHead'
    ) {
      setIsDesignerTeamAssignmentOpen(true);
    } else if (
      projectStatus === 'execution-phase' &&
      !hasExecutionTeamAssigned &&
      currentUserRole === 'executionHead'
    ) {
      setIsExecutionTeamTaskAssignmentOpen(true);
    }
  }, [hasTeamAssigned, hasExecutionTeamAssigned, projectStatus]);

  const handleLastTaskComplete = (taskData) => {
    // This could be used for additional logic if needed
    // console.log('Meeting data:', taskData);
    setTaskIdFromPopup(taskData)
    setIsDesignerAssignmentOpen(true);
  };

  const handleLastTaskTeamComplete = (taskData) => {
    // This could be used for additional logic if needed
    setTaskIdFromPopup(taskData)
    setIsDesignerTeamTaskAssignmentOpen(true);
  };

  const handleLastTaskDesignPhaseComplete = (taskData) => {
    // This could be used for additional logic if needed
    setTaskIdFromPopup(taskData)
    setIsExecutionTeamAssignmentOpen(true);
  };

  if (isLoading) {
    return <div className="loading-container p-8 text-center">
      <div className="animate-pulse text-lg">Loading tasks...</div>
    </div>;
  }

  if (error) {
    return <div className="error-container p-8 bg-red-50 text-red-600 rounded">
      Error: {error}
    </div>;
  }

  // Get current active stage based on project status
  const getCurrentStageId = () => {
    if (projectStatus === 'design-phase') return 'design';
    if (projectStatus === 'production-phase') return 'production';
    return 'sales'; // default
  };

  const currentStageId = getCurrentStageId();

  return (
    <div className="max-w-3xl mx-auto mt-10 p-4" style={{ borderRadius: 10, color: '#000', overflowY: 'auto', minHeight: '68vh', maxHeight: '68vh' }}>
      <div className="flex justify-between items-center mb-6">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
          <h2 className="text-2xl font-semibold">Project Tasks</h2>
          <TaskFilter
            onFilterChange={setTaskFilter}
            currentFilter={taskFilter}
          />
        </div>
        {/* <div className="text-sm px-3 py-1 rounded" style={{
          backgroundColor: stages.find(s => s.id === currentStageId)?.color || '#ccc',
          color: 'white'
        }}>
          Current: {projectStatus === 'sales-phase' ? 'Sales Phase' : projectStatus === 'design-phase' ? 'Design Phase' : 'Production Phase'}
        </div> */}
      </div>

      {stages.map(stage => (
        <StageAccordion
          key={stage.id}
          title={stage.title}
          tasks={stage.tasks}
          taskUpdatedDur={taskUpdatedDur}
          stageColor={stage.color}
          projectId={projectId}
          currentUserUid={currentUserUid}
          currentUserRole={currentUserRole}
          activeTaskId={activeTaskId}
          hasTaskTeamDefined={hasTaskTeamDefined}
          onLastTaskComplete={handleLastTaskComplete}
          onQuickMeeting={handleLastTaskTeamComplete}
          onExecutionHead={handleLastTaskDesignPhaseComplete}
          completedStagePercentage={completedStagePercentage}
          currentActiveStage={currentActiveStage}
          projectStatus={projectStatus}
        />
      ))}

      <DesignerAssignmentPopup
        isOpen={isDesignerAssignmentOpen}
        onClose={() => setIsDesignerAssignmentOpen(false)}
        onAssign={handleDesignerAssignment}
        isLoading={mutations.assignDesigner.isLoading}
      />

      <TeamAssignmentPopup
        isOpen={isDesignerTeamAssignmentOpen}
        onClose={() => setIsDesignerTeamAssignmentOpen(false)}
        onAssign={handleDesignerTeamAssignment}
        isLoading={mutations.assignDesignerTeam.isLoading}
      />

      <TeamTaskAssignmentPopup
        isOpen={isDesignerTeamTaskAssignmentOpen}
        onClose={() => setIsDesignerTeamTaskAssignmentOpen(false)}
        onAssignTeamTask={handleDesignerTeamTaskAssignment}
        isLoading={mutations.assignDesignerTeamTask.isLoading}
      />

      <ExecutionHeadAssignmentPopup
        isOpen={isExecutionTeamAssignmentOpen}
        onClose={() => setIsExecutionTeamAssignmentOpen(false)}
        onAssign={handleExecutionHeadAssignment}
        isLoading={mutations.assignDesigner.isLoading}
      />

      <ExecutionTeamTaskAssignmentPopup
        isOpen={isExecutionTeamTaskAssignmentOpen}
        onClose={() => setIsExecutionTeamTaskAssignmentOpen(false)}
        onAssignTeamTask={handleExecutionTeamTaskAssignment}
        projectManagerData={tasksData.assignments.projectManager}
        isLoading={mutations.assignDesignerTeamTask.isLoading}
      />
    </div>
  );
};

export default SalesTaskList;

//

// Optional: If you want to include some custom CSS for the select box
const styles = `
.custom-select-container {
  margin-bottom: 15px;
}

.custom-select {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  appearance: none;
  background-color: white;
  background-image: 
    linear-gradient(45deg, transparent 50%, gray 50%),
    linear-gradient(135deg, gray 50%, transparent 50%);
  background-position: 
    calc(100% - 20px) calc(1em + 2px),
    calc(100% - 15px) calc(1em + 2px);
  background-size: 5px 5px, 5px 5px;
  background-repeat: no-repeat;
}

.custom-select:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0,123,255,.25);
}

.custom-select:disabled {
  background-color: #e9ecef;
  opacity: 1;
}

.task-filter-container .form-select {
  font-size: 14px;
  padding: 4px 8px;
  height: auto;
}

.task-filter-container .form-select:focus {
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
  border-color: #86b7fe;
}

.custom-flex {
  display: flex;
  align-items: center;
  gap: 1rem; /* Bootstrap gap-3 = 1rem (16px) */
}

`;